    import React from 'react';
    import { Modal, Tooltip, message } from 'antd';
    import { useDrag, useDrop } from 'react-dnd';
    import { isValid, isValidArray } from './../../utils/utilities';
    import { roundNumber } from './../../utils/math.utils';
    import { SeatClickTicketPopUp } from './../../components/ticket-summary-components/seat-click-ticket-popup.component';
    import { ShowMultiplePassengers,getPassengerList } from './show-multiple-passengers.component';
    import { ACTION_SEAT_NUMBER_UPDATE_ALLOWED, ACTION_BOOK_BBO_TICKET_ALLOWED, ACTION_BOOK_BBA_TICKET_ALLOWED } from './../../utils/constant';
    import { getAPIDateFormat, getTimeFormat,getCurrentDateInIST } from './../../utils/date.utils';
    import { onHoverSeatTicketId, onExpandSharedSeat } from '../../actions/passenger-form.action';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { hoverSeatTicketIdSelector, expandedSharedSeatNumberSelector } from '../../selector/passenger-form.selector';
import { RoofExitIcon, RearExitIcon } from '../../ui-components/custom-icons';

// iOS Safari fires synthetic mouseenter+mouseleave around a single tap, which
// makes a hover-triggered Tooltip open and close in the same instant. Android
// Chrome doesn't have this problem (the tap-as-hover sticks around), so we
// only special-case iPhone/iPad here and leave Android + desktop untouched.
const IS_IOS = typeof navigator !== 'undefined' &&
    typeof window !== 'undefined' &&
    (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        // iPadOS 13+ reports as MacIntel but has touch points
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ) &&
    !window.MSStream;

const getSeatShownPassenger = (passengers) => {
     for(let passenger of passengers){
        if(passenger.isBookingAllowed == false){
            return passenger;
        }
     }
     return passengers[0];
}

    const isBookedAfterDeparture = (seat) => {
        const { start_date, start_time, booking_time, firstStop } = seat;

        if(isValid(booking_time)){
            let bookingDateTime = getCurrentDateInIST(booking_time);
            const bookingDate = getAPIDateFormat(bookingDateTime);

            let departureTime = start_time;
            let departureDate = getAPIDateFormat(start_date);
            if(isValid(firstStop) && isValid(firstStop.timing)){
                departureTime = firstStop.timing;
            }

            if(bookingDate >= departureDate){
                const bookingTime = getTimeFormat(bookingDateTime);

                if(bookingTime > departureTime){
                    return true;
                }else{
                    return false;
                }
            }else{
                return false;
            }
        }
        return false;
    }

    const getCategoryClassNameFromStatus = (seat_status, blocked_by) => {
        let classNameSeatStatus = '' , selfClassName = '';
        if(seat_status === "Blocked"){
             classNameSeatStatus = 'blocked';
        }else if(seat_status === "Confirmed"){
            classNameSeatStatus = 'booked';
        }

        if(blocked_by == global.userName){
            selfClassName = 'self';
        }else{
            selfClassName = 'others';
        }
        return `${selfClassName}-${classNameSeatStatus}`;
    }

    const getSeatCategoryClassName = (seat) => {
        let {blocked_by,seat_status} = seat;

        if(isValid(seat.passengers)&&seat.passengers.length > 0){
            let passenger = getSeatShownPassenger(seat.passengers);
            blocked_by = passenger.blocked_by;
        }

        return getCategoryClassNameFromStatus(seat_status, blocked_by);
    }

    // For a single passenger of a shared seat, use that passenger's own status/blocker
    // so each split card reflects its real booking (e.g. a Blocked sub-segment is not
    // painted with the seat-level Confirmed color).
    const getPassengerCategoryClassName = (passenger, seat) => {
        let seat_status = isValid(passenger.traveller_status) ? passenger.traveller_status : seat.seat_status;
        return getCategoryClassNameFromStatus(seat_status, passenger.blocked_by);
    }

    const getHoverClassName = (hoverSeatTicketId,seatTicketId) => {
        if(isValid(hoverSeatTicketId)&&isValid(seatTicketId)){
            if(hoverSeatTicketId === seatTicketId){
                return 'hover-seat';
            }
        }
        return '';
    }

    const getRescheduledPnr = (seat) => {
        if (isValid(seat.rescheduled_pnr)) {
            return seat.rescheduled_pnr;
        }
        return null;
    }

    export const Seat = props => {
        const {
            classname,
            object: seat,
            onSeatCLick,
            onSeatDrop,
            onNotBoardedDrop,
            isSelected,
            showFarePopOver = false,
            dontAllowSelection = false,
            showFareInLayout = false,
            changeOldLayout = false,
            oldSelectedSeats = [],
            seatMapping = {},
            isDragDropRequired = true
        } = props;

        const dispatch = useDispatch();

        const hoverSeatTicketId = useSelector(hoverSeatTicketIdSelector);
        const expandedSharedSeatNumber = useSelector(expandedSharedSeatNumberSelector);

        // State to control tooltip visibility
        const [tooltipVisible, setTooltipVisible] = React.useState(false);
        const [fareTooltipVisible, setFareTooltipVisible] = React.useState(false);

        var hoverClassName = getHoverClassName(hoverSeatTicketId,seat.ticket_id);
        var seatCategoryClassName = getSeatCategoryClassName(seat);

        const isDragDropAllowed = isDragDropRequired && isValid(global.allowedActions?.[ACTION_SEAT_NUMBER_UPDATE_ALLOWED]);

        const isConfirmedAndBlockedTicket = ['Confirmed', 'Blocked'].includes(seat.seat_status) && isValid(seat.ticket_id);
        const isConfirmedOrBlocked = ['Confirmed', 'Blocked'].includes(seat.seat_status);
        const isVacant = !isConfirmedOrBlocked;

        // Fare shown under the seat number in the layout. For confirmed/blocked
        // seats show the net (post-discount) fare so it matches the "Total Fare"
        // in the passenger tooltip; vacant/undiscounted seats keep the base fare.
        const getLayoutFare = () => {
            if (isConfirmedOrBlocked) {
                if (isValid(seat.discounted_cost_without_gst)) {
                    return roundNumber(seat.discounted_cost_without_gst);
                }
                if (seat.direct_discount > 0) {
                    return roundNumber(seat.fare - seat.direct_discount);
                }
            }
            return seat.fare;
        };
        const isMultiplePassengers = isValidArray(seat.passengers) && seat.passengers.length > 1;

        // Seat-sharing seat: the same confirmed seat is shared by more than one
        // passenger (only possible when the trip has seat sharing enabled). These
        // seats get the new single-click-to-expand experience. Single-passenger
        // seats and non-shared trips keep their existing behavior untouched.
        const isSharedSeat = isMultiplePassengers && seat.seat_status === "Confirmed" &&
            isDragDropRequired && !changeOldLayout;
        const isThisSeatExpanded = isSharedSeat && expandedSharedSeatNumber === seat.seat_number;
        // Another shared seat is expanded — dim/blur this seat to focus the user.
        const isAnotherSeatExpanded = isValid(expandedSharedSeatNumber) &&
            expandedSharedSeatNumber !== seat.seat_number;

        const isBookedAfterDepart = isBookedAfterDeparture(seat);

        const rescheduledPnr = getRescheduledPnr(seat);

        const boardedElement = React.useMemo(() => {        
            if (!seat.boarded) return null;
            return seat.sleeper ?
            <div style={{position:'relative',fontSize:'8px'}}>👋</div> :
            <div style={{position:'relative',fontSize:'8px'}}>👋</div>;
        }, [seat.boarded, seat.sleeper, seat.gender]);

        // Validate gender compatibility
        const isGenderCompatible = (draggedSeat, targetSeat) => {
            if ((draggedSeat.gender == 'M' && targetSeat.ladies_seat) || (draggedSeat.gender == 'F' && targetSeat.gents_seat)) {
                let adjacentSeat = seatMapping[targetSeat.adjacent_seat_number];
                if (adjacentSeat.ticket_id == draggedSeat.ticket_id) {
                    return true;
                }
                else {
                    return false;
                }
            }
            return true;
        };

        const getGender=(seatGender)=>{
            if(isValid(seatGender)&&seatGender==="F"){
                return "("+seatGender+")";
            }
            else{
                return '';
            }
        }
        
        // Drag configuration for Confirmed/Blocked seats
        const [{ isDragging }, drag] = useDrag({
            type: 'SEAT',
            item: () => isConfirmedAndBlockedTicket && !isMultiplePassengers? seat : null,
            canDrag: () => isDragDropAllowed && isConfirmedAndBlockedTicket && !isMultiplePassengers,
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            })
        });

        const isSameSeatNumber = (draggedItem) =>
            String(draggedItem.seat_number) === String(seat.seat_number);

        // Drop configuration for vacant seats
        const [{ isOver }, drop] = useDrop({
            accept: ['SEAT', 'NOT_BOARDED_SEAT'],
            canDrop: (draggedItem, monitor) => {
                const itemType = monitor.getItemType();
                if (itemType === 'NOT_BOARDED_SEAT') {
                    return isVacant;
                }
                return isDragDropAllowed &&
                    isVacant &&
                    !isSameSeatNumber(draggedItem) &&
                    isGenderCompatible(draggedItem, seat);
            },
            drop: (draggedItem, monitor) => {
                const itemType = monitor.getItemType();
                if (itemType === 'NOT_BOARDED_SEAT') {
                    Modal.confirm({
                        title: 'Re-confirm Passenger',
                        content: `Re-confirm passenger${draggedItem.full_name ? ' ' + draggedItem.full_name : ''} (currently not boarded, seat ${draggedItem.seat_number}) onto seat ${seat.seat_number}?`,
                        okText: 'Yes',
                        cancelText: 'No',
                        // Returning the Promise makes antd show a loading spinner on the
                        // OK button and keeps the modal open until the API resolves/rejects.
                        onOk: () => {
                            if (!onNotBoardedDrop) {
                                return Promise.resolve();
                            }
                            return onNotBoardedDrop(draggedItem, seat);
                        }
                    });
                    return;
                }
                if (!isGenderCompatible(draggedItem, seat)) {
                    message.error('Gender restriction: Cannot move this passenger to selected seat');
                }

                Modal.confirm({
                    title: 'Confirm Seat Change',
                    content: `Are you sure you want to move seat ${draggedItem.seat_number} to ${seat.seat_number}?`,
                    onOk: () => onSeatDrop && onSeatDrop(draggedItem, seat)
                });
            },
            collect: (monitor) => ({
                isOver: monitor.isOver()
            })
        });

        // Combine drag and drop refs
        const ref = (node) => {
            drag(drop(node));
        };

        // Handle click events
        const handleClick = (e) => {
            e.stopPropagation();
            // Hide tooltips on click — except on iOS, where we want the tooltip
            // to stay visible alongside the existing click action. Android &
            // desktop already showed both on a single tap/click; iOS would
            // otherwise close the tooltip instantly because of its synthetic
            // mouseenter/mouseleave behavior around a tap.
            if (!IS_IOS) {
                setTooltipVisible(false);
                setFareTooltipVisible(false);
            }

            // Seat-sharing seat: single click expands the seat into one card per
            // passenger (and dims the rest of the layout). Clicking again collapses.
            if (isSharedSeat) {
                dispatch(onExpandSharedSeat(isThisSeatExpanded ? null : seat.seat_number));
                return;
            }

            if (seat.seat_status === "Blocked") {
                handleBlockedSeatClick(e);
                return;
            }

            if (!changeOldLayout && !isDragging && onSeatCLick && isVacant && !dontAllowSelection) {
                onSeatCLick(seat);
                return;
            }

            if (changeOldLayout && !["Confirmed", "Blocked"].includes(seat.seat_status)) {
                message.error("Cannot select empty seats for change seat layout!");
                return;
            }

            if (changeOldLayout && ["Confirmed", "Blocked"].includes(seat.seat_status)) {
                onSeatCLick(seat);
                return;
            }
        };

        const handleBlockedSeatClick = (e) => {
            const hasTicketId = isValid(seat.ticket_id);
            const isCurrentUserBlocker = global.userName === seat.blocked_by;

            if (hasTicketId) {
                if (isCurrentUserBlocker || global.allowedActions[ACTION_BOOK_BBO_TICKET_ALLOWED]) {
                    onSeatCLick(seat);
                } else {
                    message.error("You don't have permission to book seat!");
                }
            } else if (global.allowedActions[ACTION_BOOK_BBA_TICKET_ALLOWED]) {
                onSeatCLick(seat);
            } else {
                message.error("You don't have permission to book seat!");
            }
        }

        // Early return for Empty seats; featured empty positions (Toilet /
        // Emergency Exit) render a marker block instead of a plain gap
        if (seat.seat_number === "Empty") {
            // Space over a lower-deck toilet: shown as a blocked block for clarity
            if (seat.blocked_space) {
                return (
                    <div
                        className={`${seat.sleeper ? 'sleeperGap' : 'seatGap'} seat-feature-cell blocked-space`}
                        title="Occupied by the lower deck toilet"
                    />
                );
            }
            if (isValid(seat.seat_feature)) {
                const isToilet = seat.seat_feature === 'Toilet';
                return (
                    <div
                        className={`${seat.sleeper ? 'sleeperGap' : 'seatGap'} seat-feature-cell ${isToilet ? 'toilet' : 'emergency-exit'}`}
                        title={seat.seat_feature}
                    >
                        {isToilet ? (
                            <React.Fragment>
                                <svg className="feature-icon" viewBox="0 0 24 24" aria-label="Toilet" role="img">
                                    <path d="M6.5 3a1.6 1.6 0 1 1 0 3.2A1.6 1.6 0 0 1 6.5 3Zm11 0a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2ZM5 7h3c.83 0 1.5.67 1.5 1.5V13H8.4v7H7v-5.5h-1V20H4.6v-7H3.5V8.5C3.5 7.67 4.17 7 5 7Zm11.2 0h2.6c.62 0 1.17.4 1.36.99L21.5 13h-1.9l1 4h-1.85v3h-1.5v-3H15.4l1-4h-1.9l1.34-5.01c.19-.59.74-.99 1.36-.99Z"/>
                                    <rect x="11.55" y="2.5" width="0.9" height="19" rx="0.45"/>
                                </svg>
                                <span className="feature-text">Toilet</span>
                            </React.Fragment>
                        ) : seat.exit_position === 'roof' ? (
                            <span className="seat-feature-exit-badge roof" title="Emergency Exit - Roof" aria-label="Emergency Exit - Roof">
                                <RoofExitIcon size={24} className="roof-exit-icon" />
                                <span className="feature-text">Roof Exit</span>
                            </span>
                        ) : (
                            <React.Fragment>
                                {(seat.exit_position || 'bottom') === 'bottom' &&
                                    <RearExitIcon size={24} className="rear-exit-icon" />
                                }
                                <span className={`seat-feature-exit-badge ${seat.exit_position || 'bottom'}`}>
                                    <span className="exit-caret" />
                                    <span className="feature-text">Emergency Exit</span>
                                </span>
                                <span className={`seat-exit-line ${seat.exit_position || 'bottom'}`} />
                            </React.Fragment>
                        )}
                    </div>
                );
            }
            return <div className={seat.sleeper ? 'sleeperGap' : 'seatGap'} />;
        }

        let showGender = ['Blocked','Confirmed'].includes(seat.seat_status);

        // Create dynamic flash animation using the seat color
        const createFlashAnimation = (color) => {
            if (!color) return 'flash-generic 1s infinite';
            
            // Create a unique animation name based on the color
            const animationName = `flash-${color.replace('#', '').replace(/[^a-zA-Z0-9]/g, '')}`;
            
            // Add the animation to the document if it doesn't exist
            if (!document.querySelector(`style[data-animation="${animationName}"]`)) {
                const style = document.createElement('style');
                style.setAttribute('data-animation', animationName);
                style.textContent = `
                    @keyframes ${animationName} {
                        0% { 
                            background-color: white; 
                            opacity: 1;
                        }
                        50% { 
                            background-color: ${color}; 
                            opacity: 0.8;
                        }
                        100% { 
                            background-color: white; 
                            opacity: 1;
                        }
                    }
                `;
                document.head.appendChild(style);
            }
            
            return `${animationName} 1s infinite`;
        };

        let styles = {
            opacity: oldSelectedSeats.includes(seat.seat_number)||isDragging ? 0.5 : 1,
            cursor: isDragDropAllowed && isConfirmedAndBlockedTicket ? 'move' : 'pointer',
            backgroundColor: seat.seat_status_color,
        };
        
        if(isBookedAfterDepart){
            styles.animation = createFlashAnimation(seat.seat_status_color);
        }

        const handleTooltipContentClick = (e) => {
            // Check if user is selecting text (text selection exists)
            const selection = window.getSelection();
            if (selection && selection.toString().length > 0) {
                // User is selecting text, don't close tooltip
                return;
            }

            // Close tooltip only on single click without text selection
            setTooltipVisible(false);
            dispatch(onHoverSeatTicketId(null));
        };

        const handleFareTooltipContentClick = (e) => {
            // Check if user is selecting text
            const selection = window.getSelection();
            if (selection && selection.toString().length > 0) {
                return;
            }

            setFareTooltipVisible(false);
        };

        const collapseSharedSeat = (e) => {
            if (e) {
                e.stopPropagation();
            }
            dispatch(onExpandSharedSeat(null));
        };

        // One expanded card per passenger of a shared seat. Hover shows that
        // passenger's details only; double click opens just that passenger's ticket.
        const renderSharedPassengerCard = (passenger, index) => {
            const cardSeat = {
                ...seat,
                passengers: [passenger],
                ticket_id: passenger.ticket_id,
                agent_id: passenger.agent_id,
                seat_status: isValid(passenger.traveller_status) ? passenger.traveller_status : seat.seat_status
            };
            // Each card shows its own passenger's status colour, not the seat-level one.
            const passengerCategoryClassName = getPassengerCategoryClassName(passenger, seat);
            const passengerColor = isValid(passenger.seat_status_color) ? passenger.seat_status_color : seat.seat_status_color;
            // A blocked sub-segment is not bookable from here: block the ticket popup and
            // explain why instead of opening it on double click.
            const isBlockedPassenger = passenger.traveller_status === "Blocked";
            // Slight alternating tilt so the cards look fanned out.
            const tilt = index % 2 === 0 ? -7 : 7;
            const cardContent = (
                <Tooltip
                    title={
                        <div onClick={handleTooltipContentClick}>
                            {getPassengerList(cardSeat, [passenger])}
                        </div>
                    }
                    trigger={IS_IOS ? 'click' : 'hover'}
                    placement="left"
                >
                    <div
                        className={`${classname} ${passengerCategoryClassName} shared-seat-card`}
                        style={{ transform: `rotate(${tilt}deg)` }}
                    >
                        <span className="shared-seat-card-fill" style={{ backgroundColor: passengerColor }} />
                        <div className="seat-number">{seat.seat_number}</div>
                        <div className="passengerCountBlock">
                            <div className="passengerCount">{index + 1}</div>
                        </div>
                    </div>
                </Tooltip>
            );
            if (isBlockedPassenger) {
                const warnBlocked = (e) => {
                    if (e) e.stopPropagation();
                    let route = isValid(passenger.origin) && isValid(passenger.destination)
                        ? ` for ${passenger.origin} - ${passenger.destination}` : '';
                    message.warning(`Seat ${seat.seat_number} is blocked${route}, booking not allowed for the searched route.`);
                };
                return (
                    <div key={passenger.ticket_id || index} onClick={warnBlocked} onDoubleClick={warnBlocked}>
                        {cardContent}
                    </div>
                );
            }
            return (
                <SeatClickTicketPopUp
                    key={passenger.ticket_id || index}
                    ticketId={[passenger.ticket_id]}
                    seatNumber={seat.seat_number}
                    agentId={passenger.agent_id}
                >
                    {cardContent}
                </SeatClickTicketPopUp>
            );
        };

        const seatContent = (
            <div
                ref={ref}
                className={`${isSharedSeat ? '' : hoverClassName} ${classname} ${seatCategoryClassName} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${oldSelectedSeats.includes(seat.seat_number) ? 'opacity-seat' : ''} ${isAnotherSeatExpanded ? 'shared-seat-dimmed' : ''} ${isThisSeatExpanded ? 'shared-seat-active' : ''}`}
                style={styles}
                onClick={handleClick}
                data-testid={seat.seat_number}
                id={seat.seat_number}
            >
                {isValid(rescheduledPnr) && (
                    <span
                        className="rescheduled-badge"
                        title={`Rescheduled from PNR ${rescheduledPnr}`}
                        aria-label={`Rescheduled from PNR ${rescheduledPnr}`}
                    >
                        R
                    </span>
                )}
                {seat.seat_feature === 'Emergency Exit' && (
                    seat.exit_position === 'roof' ? (
                        <span
                            className="seat-feature-exit-badge roof"
                            title="Emergency Exit - Roof"
                            aria-label="Emergency Exit - Roof"
                        >
                            <RoofExitIcon size={24} className="roof-exit-icon" />
                            <span className="feature-text">Roof Exit</span>
                        </span>
                    ) : (
                        <React.Fragment>
                            <span
                                className={`seat-feature-exit-badge ${seat.exit_position || 'bottom'}`}
                                title="Emergency Exit"
                                aria-label="Emergency Exit"
                            >
                                <span className="exit-caret" />
                                <span className="feature-text">Emergency Exit</span>
                            </span>
                            <span className={`seat-exit-line ${seat.exit_position || 'bottom'}`} />
                        </React.Fragment>
                    )
                )}
                <div className="seat-number" style={{display:'flex',alignItems:'center',justifyContent:'space-around'}}>
                    {seat.seat_number}
                    {!seat.sleeper ? boardedElement : null}
                </div>
                {showFareInLayout && (
                    <div className="seat-fare">{getLayoutFare()}</div>
                )}
                {seat.sleeper ? boardedElement : null}
                {showGender&&seat.sleeper ? (
                    <div className="seat-gender" >{getGender(seat.gender)}</div>
                ) : null}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-around'}}>
                    {isMultiplePassengers && seat.passengers.length > 1 && (
                        <ShowMultiplePassengers seat={seat} />
                    )}
                    {showGender&&!seat.sleeper ? (
                        <div className="seat-gender">{getGender(seat.gender)}</div>
                    ) : null}
                </div>

                {isThisSeatExpanded && (
                    <React.Fragment>
                        {/* Click anywhere outside the cards to collapse */}
                        <div className="shared-seat-backdrop" onClick={collapseSharedSeat} />
                        <div
                            className="shared-seat-expansion"
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                        >
                            {seat.passengers.map(renderSharedPassengerCard)}
                        </div>
                    </React.Fragment>
                )}

            </div>
        );


        const wrappedContent = () => {
            // While expanded, the per-passenger cards carry their own tooltips;
            // skip the combined "all passengers" hover tooltip to avoid overlap.
            if (isThisSeatExpanded) {
                return seatContent;
            }
            // Shared trip with hide_booking_from_child: other operators' booking details are hidden
            if (seat.hide_booking_details == 1) {
                return seatContent;
            }
            if (!isDragging && (["Blocked", "Confirmed"].includes(seat.seat_status) || (changeOldLayout && isConfirmedOrBlocked))) {
                return (
                    <Tooltip
                        title={
                            <div onClick={handleTooltipContentClick}>
                                {getPassengerList(seat,seat.passengers)}
                            </div>
                        }
                        trigger={IS_IOS ? 'click' : 'hover'}
                        visible={tooltipVisible}
                        onVisibleChange={(visible) => {
                            setTooltipVisible(visible);
                            if(visible){
                                dispatch(onHoverSeatTicketId(seat.ticket_id));
                            }else{
                                dispatch(onHoverSeatTicketId(null));
                            }
                        }}
                        placement="left"
                    >
                        {seatContent}
                    </Tooltip>
                );
            }
            else {
                return showFarePopOver && !isDragging ? (
                    <Tooltip 
                        title={
                            <div onClick={handleFareTooltipContentClick}>
                                {`Fare: ${seat.fare}`}
                            </div>
                        } 
                        trigger="hover"
                        visible={fareTooltipVisible}
                        onVisibleChange={setFareTooltipVisible}
                    >
                        {seatContent}
                    </Tooltip>
                ) : seatContent;
            }
        }

        // Show ticket popup only for Confirmed seats. Shared (multi-passenger)
        // seats are excluded here — their ticket popup is opened per passenger
        // from the expanded cards instead of as one combined double-click popup.
        if (isDragDropRequired && !changeOldLayout && seat.seat_status === "Confirmed" && isPassengerEntryExits(seat) && !isSharedSeat) {
            return (
                <SeatClickTicketPopUp
                    ticketId={getTicketIds(seat)}
                    seatNumber={seat.seat_number}
                    agentId={seat.agent_id}
                >
                    {wrappedContent()}
                </SeatClickTicketPopUp>
            );
        }

        return wrappedContent();
    };

    // Helper functions remain the same
    const isPassengerEntryExits = (object) => {
        const { full_name, passengers } = object;
        if (isValid(full_name)) return true;
        return isValidArray(passengers) && passengers.some(ele => isValid(ele.full_name));
    };

    const getTicketIds = (seat) => {
        const { passengers, ticket_id } = seat;
        if (isValidArray(passengers)) {
            return passengers.map(p => p.ticket_id);
        }
        return [ticket_id];
    };
